import { sessionWallClockMs } from './availability.js';
export { ADMIN_STAFF_ROLES, ADMIN_PERMISSIONS, ROLE_PERMISSIONS, hasAdminPermission, getPermissionsForRole, } from './rbac.js';
export const THERAPIST_SPECIALIZATIONS = [
    'anxiety',
    'depression',
    'trauma_ptsd',
    'grief_loss',
    'relationship_couples',
    'family_therapy',
    'addiction_substance',
    'stress_burnout',
    'ocd',
    'eating_disorders',
    'adolescent_youth',
    'child_therapy',
    'lgbtq',
    'career_work',
    'anger_management',
    'self_esteem',
    'bipolar',
    'schizophrenia',
    'personality_disorders',
    'sexual_health',
    'domestic_violence',
    'chronic_illness',
    'sleep_disorders',
    'faith_spiritual',
];
/** Human-readable labels for therapist specialization enums */
export const SPECIALIZATION_LABELS = {
    anxiety: 'Anxiety',
    depression: 'Depression',
    trauma_ptsd: 'Trauma / PTSD',
    grief_loss: 'Grief & Loss',
    relationship_couples: 'Relationship & Couples',
    family_therapy: 'Family Therapy',
    addiction_substance: 'Addiction & Substance',
    stress_burnout: 'Stress & Burnout',
    ocd: 'OCD',
    eating_disorders: 'Eating Disorders',
    adolescent_youth: 'Adolescent & Youth',
    child_therapy: 'Child Therapy',
    lgbtq: 'LGBTQ+',
    career_work: 'Career & Work',
    anger_management: 'Anger Management',
    self_esteem: 'Self Esteem',
    bipolar: 'Bipolar',
    schizophrenia: 'Schizophrenia',
    personality_disorders: 'Personality Disorders',
    sexual_health: 'Sexual Health',
    domestic_violence: 'Domestic Violence',
    chronic_illness: 'Chronic Illness',
    sleep_disorders: 'Sleep Disorders',
    faith_spiritual: 'Faith & Spiritual',
};
export function resolveTherapistDisplayName(displayName, email, bankAccountName) {
    if (displayName?.trim())
        return displayName.trim();
    if (bankAccountName?.trim())
        return bankAccountName.trim();
    const local = email.split('@')[0] ?? 'Therapist';
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
}
/** First name for client greetings — uses saved firstName, else a title-cased email local-part. */
export function resolveClientGreetingName(firstName, email, fallback = 'there') {
    const trimmed = firstName?.trim();
    if (trimmed) {
        const [first] = trimmed.split(/\s+/);
        return first ?? trimmed;
    }
    const local = email.split('@')[0];
    if (!local)
        return fallback;
    const [segment] = local.split(/[._-]+/).filter(Boolean);
    if (!segment)
        return fallback;
    return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
}
/** Name shown to the treating therapist: saved first and last name, otherwise the greeting name. */
export function clientFullName(firstName, lastName, email) {
    const parts = [firstName, lastName]
        .map((part) => part?.trim())
        .filter((part) => Boolean(part));
    if (parts.length > 0)
        return parts.join(' ');
    if (email)
        return resolveClientGreetingName(null, email, 'Client');
    return 'Client';
}
export function formatSpecialization(value) {
    if (THERAPIST_SPECIALIZATIONS.includes(value)) {
        return SPECIALIZATION_LABELS[value];
    }
    return value
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
/** Comma-separated human-readable specialization list */
export function formatSpecializationsList(values, limit) {
    const slice = limit ? values.slice(0, limit) : values;
    const labels = slice.map(formatSpecialization);
    if (limit && values.length > limit) {
        return `${labels.join(', ')} +${values.length - limit} more`;
    }
    return labels.join(', ');
}
export const DAY_NAME_TO_INDEX = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
};
export const VIDEO_PROVIDERS = ['daily', 'agora'];
export const SESSION_TYPES = ['video', 'voice', 'chat'];
export const SESSION_TYPE_LABELS = {
    video: 'Video',
    voice: 'Audio',
    chat: 'Chat',
};
export const DEFAULT_SESSION_MODALITIES = ['video', 'voice', 'chat'];
export function sessionUsesDailyRoom(sessionType) {
    return sessionType !== 'chat';
}
export function getSessionJoinWebPath(bookingId, sessionType) {
    return sessionType === 'chat' ? `/chat/${bookingId}` : `/session/${bookingId}`;
}
export function getSessionJoinMobileScreen(sessionType) {
    return sessionType === 'chat' ? 'Chat' : 'Session';
}
export function getSessionJoinLabel(sessionType) {
    if (sessionType === 'chat')
        return 'Open Chat';
    if (sessionType === 'voice')
        return 'Join Audio';
    return 'Join Video';
}
export function getDailyRoomJoinUrl(roomUrl, sessionType) {
    if (sessionType !== 'voice')
        return roomUrl;
    const separator = roomUrl.includes('?') ? '&' : '?';
    return `${roomUrl}${separator}startVideoOff=true`;
}
/** Minutes before scheduled start when video/voice join opens. */
export const SESSION_JOIN_OPEN_MINUTES_BEFORE = 15;
/** Minutes after scheduled start when join remains open. */
export const SESSION_JOIN_OPEN_MINUTES_AFTER = 90;
export function isSessionJoinWindowOpen(scheduledAt, now = new Date()) {
    const start = sessionWallClockMs(scheduledAt);
    if (Number.isNaN(start))
        return false;
    const openAt = start - SESSION_JOIN_OPEN_MINUTES_BEFORE * 60_000;
    const closeAt = start + SESSION_JOIN_OPEN_MINUTES_AFTER * 60_000;
    const t = now.getTime();
    return t >= openAt && t <= closeAt;
}
/** Confirmed sessions stay on upcoming lists until the time on the app has passed and join has closed. */
export function isSessionOnUpcomingList(scheduledAt, now = new Date()) {
    const start = sessionWallClockMs(scheduledAt);
    if (Number.isNaN(start))
        return false;
    return start > now.getTime() || isSessionJoinWindowOpen(scheduledAt, now);
}
/** True when the URL is a real Daily room (not a deferred/fallback placeholder). */
export function isUsableDailyRoomUrl(url) {
    if (!url)
        return false;
    return !url.includes('/pending-') && !url.includes('/fallback-');
}
export function normalizeSessionModalities(values) {
    const normalized = values.filter((v) => SESSION_TYPES.includes(v));
    if (normalized.length === 0) {
        throw new Error('At least one session format (video, voice, or chat) must be enabled');
    }
    return [...new Set(normalized)];
}
export const THERAPIST_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'yo', label: 'Yoruba' },
    { code: 'ig', label: 'Igbo' },
    { code: 'ha', label: 'Hausa' },
    { code: 'pcm', label: 'Pidgin' },
];
export const THERAPIST_LANGUAGE_CODES = THERAPIST_LANGUAGES.map((l) => l.code);
const LANGUAGE_LABEL_BY_CODE = Object.fromEntries(THERAPIST_LANGUAGES.map((l) => [l.code, l.label]));
const LANGUAGE_CODE_BY_LABEL = Object.fromEntries(THERAPIST_LANGUAGES.map((l) => [l.label, l.code]));
/** Display a stored language code or legacy label as a human-readable name */
export function formatTherapistLanguage(value) {
    const label = LANGUAGE_LABEL_BY_CODE[value];
    if (label)
        return label;
    if (LANGUAGE_CODE_BY_LABEL[value])
        return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
}
export function formatTherapistLanguages(values) {
    return values.map(formatTherapistLanguage).join(', ');
}
/** Normalize therapist language selections to ISO-style codes stored in the database */
export function normalizeTherapistLanguageCodes(values) {
    const normalized = values.map((value) => {
        if (THERAPIST_LANGUAGE_CODES.includes(value)) {
            return value;
        }
        const fromLabel = LANGUAGE_CODE_BY_LABEL[value];
        if (fromLabel)
            return fromLabel;
        throw new Error(`Invalid language: ${value}`);
    });
    return [...new Set(normalized)];
}
/** Convert stored codes to display labels for therapist settings forms */
export function therapistLanguageCodesToLabels(codes) {
    return codes.map((code) => formatTherapistLanguage(code));
}
export const MOOD_LEVELS = ['awful', 'low', 'okay', 'good', 'great'];
export const MOOD_EMOJI = {
    awful: '😫',
    low: '😔',
    okay: '😐',
    good: '🙂',
    great: '😄',
};
export const MOOD_LABELS = {
    awful: 'Awful',
    low: 'Low',
    okay: 'Okay',
    good: 'Good',
    great: 'Great',
};
//# sourceMappingURL=types.js.map